"use client";

import { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/lib/firebase";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import {
  PageWrapper,
  Container,
  Card,
  Title,
  Subtitle,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  ErrorMessage,
  SuccessMessage,
  BackLink,
  InfoBox,
} from "./page.styles";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      // Configure the action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setSuccess(true);
      setEmail("");
    } catch (err: any) {
      console.error("Password reset error:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);

      // Handle different error types
      if (err.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else if (err.code === "auth/missing-continue-uri") {
        setError("Configuration error. Please contact support.");
      } else if (err.code === "auth/invalid-continue-uri") {
        setError("Configuration error. Please contact support.");
      } else if (err.code === "auth/unauthorized-continue-uri") {
        setError("Configuration error. Please contact support.");
      } else {
        setError(
          `Failed to send reset email: ${err.message || "Please try again."}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <PublicHeader />
      <Container>
        <Card>
          <Title>Reset Password</Title>
          <Subtitle>
            Enter your email address and we&apos;ll send you a link to reset
            your password
          </Subtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <>
              <SuccessMessage>
                Password reset email sent successfully! Check your inbox (and
                spam folder) for instructions.
              </SuccessMessage>
              <InfoBox>
                <strong>Note:</strong> If you don&apos;t receive the email
                within 5 minutes:
                <br />
                • Check your spam/junk folder
                <br />
                • Make sure the email address is correct
                <br />
                • Try requesting again
                <br />• Check browser console for any error messages
              </InfoBox>
            </>
          )}

          {!success && (
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </InputGroup>

              <Button type="submit" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </Form>
          )}

          <BackLink href="/login">← Back to Login</BackLink>
        </Card>
      </Container>
      <Footer />
    </PageWrapper>
  );
}
